export interface SeriesArticle {
  num: number;
  title: string;
  slug: string;
  status: 'published' | 'upcoming';
  duration?: string;
  label?: string;
  excerpt?: string;
  date?: string;
}

export interface Series {
  id: string;
  bigTitle: string;
  seriesName: string;
  seriesSubtitle: string;
  description: string;
  goal: string;
  category: string;
  categorySlug: string;
  tags: string[];
  currentProgress: string;
  nextStep: string;
  upcomingTopics: string[];
  articles: SeriesArticle[];
}

export interface SeriesSettings {
  activeSeriesId: string;
  seriesList: Series[];
}

const STORAGE_KEY = 'series_settings';

export const defaultSeriesList: Series[] = [
  {
    id: 'build-log',
    bigTitle: 'このサイト制作',
    seriesName: '制作記録ログ',
    seriesSubtitle: 'このサイトを作った全記録',
    description: 'Readdy.aiを使ってAI実践メディアを立ち上げた全工程を記録するシリーズ。仕様決め・デザイン・実装・SEO・Supabase連携まで、判断・迷い・失敗も含めてそのまま残す。',
    goal: 'AIだけでメディアサイトを立ち上げ、継続的に運営できる状態にする',
    category: '制作記録',
    categorySlug: 'build-log',
    tags: ['Readdy.ai', 'Supabase', 'SEO', 'サイト制作'],
    currentProgress: '第4回まで公開済み。デザイン・SEO初期設定まで完了。次はSupabase連携。',
    nextStep: 'Supabaseで記事管理を繋げる（第5回）',
    upcomingTopics: [
      'Supabaseで記事管理を繋げる',
      '公開後の改善サイクルを作る',
    ],
    articles: [
      { num: 1, title: 'Readdy.aiでブログを作ることにした理由', slug: 'readdy-blog-7days-full-log', status: 'published', duration: '8 min', label: '方針決め', excerpt: 'なぜReaddy.aiを選んだのか。他の選択肢と比較した上での判断を整理する。', date: '2026-03-05' },
      { num: 2, title: 'AIとのやり取りで仕様を固める方法', slug: 'ai-spec-first-workflow', status: 'published', duration: '12 min', label: '仕様整理', excerpt: 'コードを書く前に仕様書を作ると、AIとのやり取りが劇的に変わる。その具体的な手順。', date: '2026-03-10' },
      { num: 3, title: 'デザイントークンをAIと一緒に決めた', slug: 'design-token-with-ai', status: 'published', duration: '10 min', label: 'デザイン', excerpt: '色・フォント・余白の設計をAIと対話しながら決めた過程を記録する。', date: '2026-03-18' },
      { num: 4, title: 'SEO初期設定をAIに全部やらせた', slug: 'blog-seo-with-ai', status: 'published', duration: '9 min', label: 'SEO', excerpt: 'メタタグ・構造化データ・サイトマップをAIに指示して設定した全記録。', date: '2026-03-25' },
      { num: 5, title: 'Supabaseで記事管理を繋げる', slug: '#', status: 'upcoming', label: 'DB連携', excerpt: 'Supabaseを使って記事データを管理できる状態にする。スキーマ設計から実装まで。' },
      { num: 6, title: '公開後の改善サイクルを作る', slug: '#', status: 'upcoming', label: '運用', excerpt: 'アクセス解析・フィードバック収集・改善の仕組みを整える。' },
    ],
  },
  {
    id: 'spotify-ui',
    bigTitle: 'Spotify風UI再現',
    seriesName: 'UI再現実験ログ',
    seriesSubtitle: 'AIでSpotify風UIをどこまで再現できるか',
    description: 'Spotifyのデザインと操作感を、AIだけを使ってどこまで再現できるかを検証するシリーズ。詰まったところと解決策を全部残す実験ログ。',
    goal: 'Spotify風のUIをReact + TailwindCSSで完全再現し、再現プロセスを仕様化する',
    category: 'AIツール活用',
    categorySlug: 'ai-creation',
    tags: ['UI再現', 'React', 'Cursor', 'Spotify'],
    currentProgress: '第3回まで公開済み。ナビゲーション・カード・プレイヤーUIまで完成。',
    nextStep: 'アニメーションとトランジションの再現（第4回）',
    upcomingTopics: [
      'アニメーションとトランジションの再現',
      'レスポンシブ対応の詰め',
      '全体の仕様書化とテンプレート公開',
    ],
    articles: [
      { num: 1, title: 'Spotify風UIをゼロから再現する——設計方針と最初の壁', slug: 'ui-reproduction-spotify-log', status: 'published', duration: '15 min', label: '設計', excerpt: 'どこから手をつけるか。デザインを分解して実装順序を決めた過程を記録する。', date: '2026-03-12' },
      { num: 2, title: 'ナビゲーションとサイドバーの再現——Cursorとの対話ログ', slug: 'spotify-nav-sidebar', status: 'published', duration: '11 min', label: '実装', excerpt: 'サイドバーの構造とナビゲーションの動きを再現した。詰まった箇所と解決策。', date: '2026-03-19' },
      { num: 3, title: 'カードUIとグリッドレイアウトの再現', slug: 'spotify-card-grid', status: 'published', duration: '9 min', label: '実装', excerpt: 'アルバムカードとグリッドの実装。ホバーエフェクトの再現で詰まった話。', date: '2026-03-26' },
      { num: 4, title: 'アニメーションとトランジションの再現', slug: '#', status: 'upcoming', label: 'アニメーション', excerpt: 'Spotifyらしい滑らかな動きをどう再現するか。framer-motionとCSSの使い分け。' },
      { num: 5, title: 'レスポンシブ対応の詰め', slug: '#', status: 'upcoming', label: 'レスポンシブ' },
      { num: 6, title: '全体の仕様書化とテンプレート公開', slug: '#', status: 'upcoming', label: '仕様化' },
    ],
  },
  {
    id: 'ai-article-flow',
    bigTitle: 'AI記事制作フロー',
    seriesName: 'AI記事制作フローを仕様化する',
    seriesSubtitle: 'プロンプト設計から構成・校正まで一気通貫で整理',
    description: '記事を書くプロセス自体をAIで効率化・仕様化する試み。プロンプト設計・構成・校正・公開まで、再現できる形に整理するシリーズ。',
    goal: '誰でも再現できるAI記事制作フローのテンプレートを作る',
    category: '制作フロー',
    categorySlug: 'build-log',
    tags: ['記事制作', 'プロンプト', 'ChatGPT', 'ワークフロー'],
    currentProgress: '第2回まで公開済み。テーマ選定・構成設計まで整理完了。',
    nextStep: '本文生成プロンプトの設計と検証（第3回）',
    upcomingTopics: [
      '本文生成プロンプトの設計と検証',
      '校正・編集フローの整理',
      'テンプレート公開と使い方ガイド',
    ],
    articles: [
      { num: 1, title: 'AI記事制作フローを仕様化する——全体設計と方針', slug: 'ai-article-writing-flow', status: 'published', duration: '10 min', label: '設計', excerpt: 'どんなフローを作るか。ゴールと制約を整理してから設計を始めた。', date: '2026-02-28' },
      { num: 2, title: 'テーマ選定と構成設計をAIに任せる方法', slug: 'ai-article-structure', status: 'published', duration: '8 min', label: '構成', excerpt: 'テーマ選定から記事構成まで、AIとの対話で決める手順を整理した。', date: '2026-03-08' },
      { num: 3, title: '本文生成プロンプトの設計と検証', slug: '#', status: 'upcoming', label: '本文生成' },
      { num: 4, title: '校正・編集フローの整理', slug: '#', status: 'upcoming', label: '校正' },
      { num: 5, title: 'テンプレート公開と使い方ガイド', slug: '#', status: 'upcoming', label: '公開' },
    ],
  },
];

const defaultSettings: SeriesSettings = {
  activeSeriesId: 'build-log',
  seriesList: defaultSeriesList,
};

export function getSeriesSettings(): SeriesSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SeriesSettings;
  } catch {
    // ignore
  }
  return defaultSettings;
}

export function saveSeriesSettings(settings: SeriesSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function getActiveSeries(settings: SeriesSettings): Series | null {
  return settings.seriesList.find((s) => s.id === settings.activeSeriesId) ?? null;
}

export function getSeriesById(id: string): Series | null {
  return defaultSeriesList.find((s) => s.id === id) ?? null;
}
